import { useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useQuery } from '@tanstack/react-query';

import { adminApi } from '../../services/adminApi';

const WINDOW_OPTIONS: { value: number; label: string }[] = [
  { value: 6, label: 'Last 6 hours' },
  { value: 24, label: 'Last 24 hours' },
  { value: 72, label: 'Last 3 days' },
  { value: 168, label: 'Last 7 days' },
];

function formatTimestamp(value: string | null): string {
  if (!value) return 'Never';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

/**
 * Admin scrape-health page (GitHub #12 area 5).
 *
 * Surfaces per-retailer scrape health from `GET /api/admin/scrape-results`:
 * degraded / stale flags, last-scrape time, and aggregate raw-listing counts
 * (total, recently scraped within the window, and still-unprocessed). Auth is
 * enforced upstream by AdminLayout (redirects non-admins); the server also
 * re-checks admin role.
 *
 * Privacy: every value shown is operational aggregate metadata about retailers
 * and the scrape pipeline — no end-user PII.
 */
export function ScrapeHealthPage() {
  const [windowHours, setWindowHours] = useState(24);
  const [activeOnly, setActiveOnly] = useState(false);

  const query = useQuery({
    queryKey: ['admin', 'scrape-health', { windowHours, activeOnly }],
    queryFn: () => adminApi.getScrapeHealth({ windowHours, activeOnly }),
  });

  function onWindowChange(event: SelectChangeEvent<number>) {
    setWindowHours(Number(event.target.value));
  }

  return (
    <Box>
      <Typography variant="h1" sx={{ fontSize: '1.6rem', mb: 1 }}>
        Scrape health
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Per-retailer scrape status and listing counts. Aggregate operational metadata only.
      </Typography>

      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}
      >
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="scrape-window-label">Recent window</InputLabel>
          <Select
            labelId="scrape-window-label"
            id="scrape-window"
            value={windowHours}
            label="Recent window"
            onChange={onWindowChange}
          >
            {WINDOW_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
              inputProps={{ 'aria-label': 'Show active retailers only' }}
            />
          }
          label="Active retailers only"
        />
      </Paper>

      {query.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading scrape health" />
        </Box>
      )}

      {query.isError && (
        <Alert severity="error" role="alert">
          Failed to load scrape health: {(query.error as Error).message}
        </Alert>
      )}

      {query.isSuccess && (
        <>
          <Box
            component="ul"
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(3, 1fr)' },
              gap: 2,
              listStyle: 'none',
              pl: 0,
              m: 0,
              mb: 3,
            }}
          >
            <Box component="li">
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h3" component="p" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                  {query.data.summary.totalRetailers.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Retailers
                </Typography>
              </Paper>
            </Box>
            <Box component="li">
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h3" component="p" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                  {query.data.summary.degradedRetailers.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Degraded
                </Typography>
              </Paper>
            </Box>
            <Box component="li">
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h3" component="p" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                  {query.data.summary.staleRetailers.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Stale
                </Typography>
              </Paper>
            </Box>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table aria-label="Retailer scrape health">
              <TableHead>
                <TableRow>
                  <TableCell>Retailer</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last scraped</TableCell>
                  <TableCell align="right">Listings</TableCell>
                  <TableCell align="right">Recent</TableCell>
                  <TableCell align="right">Unprocessed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.retailers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No retailers to report.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  query.data.retailers.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{r.name}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {r.isDegraded && <Chip label="Degraded" color="error" size="small" />}
                          {r.isStale && !r.isDegraded && (
                            <Chip label="Stale" color="warning" size="small" />
                          )}
                          {!r.isStale && !r.isDegraded && (
                            <Chip label="Healthy" color="success" size="small" />
                          )}
                          {!r.isActive && <Chip label="Inactive" size="small" />}
                        </Box>
                      </TableCell>
                      <TableCell>{formatTimestamp(r.lastScrapedAt)}</TableCell>
                      <TableCell align="right">{r.rawListingsTotal.toLocaleString()}</TableCell>
                      <TableCell align="right">{r.rawListingsRecent.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {r.rawListingsUnprocessed.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}

export default ScrapeHealthPage;
